import type { ContentSheetData } from '@/services/auditService/auditFsAnnualService';
import { DATE_FORMAT_HINT, normalizeToDdMmYyyy } from '@/utils/dataUtils';

const CONTENT_DATE_FIELDS: Array<{
    key: keyof ContentSheetData;
    label: string;
    required?: boolean;
}> = [
    { key: 'periodFrom', label: 'Period From', required: true },
    { key: 'periodTo', label: 'Period To', required: true },
    { key: 'lastYearPeriodFrom', label: 'Last Year Period From' },
    { key: 'lastYearPeriodTo', label: 'Last Year Period To' },
    { key: 'changeEffectiveDate', label: 'Change Effective Date' },
    { key: 'dateOfBoardResolution', label: 'Date of Board Resolution' },
    { key: 'inCorpDate', label: 'Incorporation Date' },
];

export type ContentDateNormalizeResult = {
    data: ContentSheetData;
    /** Fields auto-corrected to dd/MM/yyyy */
    corrected: string[];
    /** Non-empty values that could not be parsed */
    invalid: string[];
};

/**
 * Normalize Content sheet date fields to strict dd/MM/yyyy.
 * Required fields that are present but invalid are listed in `invalid`.
 */
export function normalizeContentSheetDates(cd: ContentSheetData): ContentDateNormalizeResult {
    const next: ContentSheetData = { ...cd };
    const corrected: string[] = [];
    const invalid: string[] = [];

    for (const { key, label, required } of CONTENT_DATE_FIELDS) {
        const raw = next[key];
        if (raw == null || String(raw).trim() === '') {
            continue;
        }
        const rawStr = String(raw).trim();
        const fixed = normalizeToDdMmYyyy(rawStr);
        if (!fixed) {
            if (required) {
                invalid.push(`${label} ("${rawStr}")`);
            } else {
                invalid.push(`${label} ("${rawStr}")`);
                (next as any)[key] = null;
            }
            continue;
        }
        if (fixed !== rawStr) {
            corrected.push(`${label}: ${rawStr} → ${fixed}`);
        }
        (next as any)[key] = fixed;
    }

    return { data: next, corrected, invalid };
}

export function formatContentDateFeedback(result: ContentDateNormalizeResult): string | null {
    const parts: string[] = [];
    if (result.corrected.length) {
        parts.push(`Dates auto-corrected to dd/MM/yyyy: ${result.corrected.join('; ')}`);
    }
    if (result.invalid.length) {
        parts.push(`${DATE_FORMAT_HINT}: ${result.invalid.join('; ')}`);
    }
    return parts.length ? parts.join('\n') : null;
}
